import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { questions } from '../utils/quizData';
import { determineResultType } from '../utils/scoring';

export default function QuizFlow() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [direction, setDirection] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '', zipCode: '', 
    propertyAddress: '', estimatedHomeValue: '', bestTime: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load saved progress
  useEffect(() => {
    const saved = localStorage.getItem('homeQuizProgress');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.answers && parsed.step < questions.length) {
          setAnswers(parsed.answers);
          setCurrentStep(parsed.step);
        }
      } catch (e) {
        // ignore invalid JSON
      }
    }
  }, []);

  // Save progress
  useEffect(() => {
    if (currentStep < questions.length) {
      localStorage.setItem('homeQuizProgress', JSON.stringify({ answers, step: currentStep }));
    }
  }, [answers, currentStep]);

  const handleOptionSelect = (questionId, value, type) => {
    if (questionId === 'homeownership' && value === 'rent') {
      navigate('/renters');
      return;
    }

    setAnswers(prev => {
      const current = prev[questionId] || [];
      if (type === 'single') {
        return { ...prev, [questionId]: [value] };
      } else {
        // Multi-select logic
        if (value === 'none' || value === 'no_risks') {
          return { ...prev, [questionId]: [value] };
        } else {
          let newArr = current.includes(value) 
            ? current.filter(v => v !== value) 
            : [...current.filter(v => v !== 'none' && v !== 'no_risks'), value];
          return { ...prev, [questionId]: newArr };
        }
      }
    });

    // Auto-advance for single choice
    if (type === 'single') {
      setTimeout(handleNext, 400);
    }
  };

  const handleNext = () => {
    if (currentStep < questions.length) {
      setDirection(1);
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setDirection(-1);
      setCurrentStep(prev => prev - 1);
    } else {
      navigate('/');
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName) newErrors.firstName = 'Required';
    if (!formData.lastName) newErrors.lastName = 'Required';
    if (!formData.email || !/^\S+@\S+\.\S+$/.test(formData.email)) newErrors.email = 'Valid email required';
    if (!formData.phone || formData.phone.length < 10) newErrors.phone = 'Valid phone required';
    if (!formData.zipCode || !/^9\d{4}$/.test(formData.zipCode)) newErrors.zipCode = 'Valid CA ZIP required (starts with 9)';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    // Simulate API Call
    setTimeout(() => {
      const resultType = determineResultType(answers);
      // Clear storage
      localStorage.removeItem('homeQuizProgress');
      // Navigate to results with state
      navigate('/results', { state: { resultType, answers, formData } });
    }, 1500);
  };

  const isQuestionAnswered = (qId) => {
    return answers[qId] && answers[qId].length > 0;
  };

  const variants = {
    enter: (direction) => ({ x: direction > 0 ? 50 : -50, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (direction) => ({ x: direction < 0 ? 50 : -50, opacity: 0 })
  };

  const renderQuestion = () => {
    const q = questions[currentStep];
    const currentAnswers = answers[q.id] || [];

    return (
      <div className="w-full max-w-2xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-2">{q.question}</h2>
        {q.subtext && <p className="text-slate-500 mb-6">{q.subtext}</p>}
        
        <div className="space-y-3 mt-8">
          {q.options.map(opt => {
            const isSelected = currentAnswers.includes(opt.value);
            return (
              <button
                key={opt.id}
                onClick={() => handleOptionSelect(q.id, opt.value, q.type)}
                className={`w-full text-left p-4 rounded-xl border-2 transition-all flex items-center justify-between ${
                  isSelected 
                    ? 'border-[#10B981] bg-green-50' 
                    : 'border-slate-200 bg-white hover:border-[#1E40AF] hover:bg-slate-50'
                }`}
              >
                <span className={`font-medium ${isSelected ? 'text-[#10B981]' : 'text-slate-700'}`}>
                  {opt.label}
                </span>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${
                  isSelected ? 'border-[#10B981] bg-[#10B981]' : 'border-slate-300'
                }`}>
                  {isSelected && <SafeIcon icon="FiCheck" className="text-white text-sm" />}
                </div>
              </button>
            );
          })}
        </div>

        {q.type === 'multiple' && (
          <div className="mt-8 flex justify-end">
            <button 
              onClick={handleNext}
              disabled={!isQuestionAnswered(q.id)}
              className={`px-8 py-3 rounded-xl font-semibold transition-all ${
                isQuestionAnswered(q.id)
                  ? 'bg-[#1E40AF] text-white hover:bg-blue-800'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
            >
              Continue
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderLeadForm = () => (
    <div className="w-full max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 text-[#10B981] mb-4">
          <SafeIcon icon="FiCheckCircle" className="text-3xl" />
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-2">Quiz Complete!</h2>
        <p className="text-slate-600">Enter your details to see your personalized coverage review.</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">First Name*</label>
            <input 
              type="text" 
              className={`w-full p-3 rounded-lg border focus:ring-2 focus:ring-[#1E40AF] outline-none ${errors.firstName ? 'border-red-500' : 'border-slate-300'}`}
              value={formData.firstName} onChange={e => setFormData({...formData, firstName: e.target.value})}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Last Name*</label>
            <input 
              type="text" 
              className={`w-full p-3 rounded-lg border focus:ring-2 focus:ring-[#1E40AF] outline-none ${errors.lastName ? 'border-red-500' : 'border-slate-300'}`}
              value={formData.lastName} onChange={e => setFormData({...formData, lastName: e.target.value})}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email Address*</label>
            <input 
              type="email" 
              className={`w-full p-3 rounded-lg border focus:ring-2 focus:ring-[#1E40AF] outline-none ${errors.email ? 'border-red-500' : 'border-slate-300'}`}
              value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number*</label>
            <input 
              type="tel" 
              placeholder="(XXX) XXX-XXXX"
              className={`w-full p-3 rounded-lg border focus:ring-2 focus:ring-[#1E40AF] outline-none ${errors.phone ? 'border-red-500' : 'border-slate-300'}`}
              value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">ZIP Code (CA Only)*</label>
            <input 
              type="text" 
              maxLength="5"
              placeholder="9XXXX"
              className={`w-full p-3 rounded-lg border focus:ring-2 focus:ring-[#1E40AF] outline-none ${errors.zipCode ? 'border-red-500' : 'border-slate-300'}`}
              value={formData.zipCode} onChange={e => setFormData({...formData, zipCode: e.target.value})}
            />
            {errors.zipCode && <p className="text-red-500 text-xs mt-1">{errors.zipCode}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Estimated Home Value</label>
            <select 
              className="w-full p-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-[#1E40AF] outline-none bg-white"
              value={formData.estimatedHomeValue} onChange={e => setFormData({...formData, estimatedHomeValue: e.target.value})}
            >
              <option value="">Select an option</option>
              <option value="under_300k">Under $300K</option>
              <option value="300k_500k">$300K - $500K</option>
              <option value="500k_750k">$500K - $750K</option>
              <option value="750k_1m">$750K - $1M</option>
              <option value="over_1m">Over $1M</option>
            </select>
          </div>
        </div>

        <button 
          type="submit" 
          disabled={isSubmitting}
          className="w-full bg-[#10B981] hover:bg-emerald-600 text-white font-bold py-4 rounded-xl shadow-lg transition-all flex items-center justify-center gap-2"
        >
          {isSubmitting ? (
            <span className="flex items-center gap-2"><SafeIcon icon="FiLoader" className="animate-spin" /> Analyzing Results...</span>
          ) : (
            <>Get My Coverage Review <SafeIcon icon="FiArrowRight" /></>
          )}
        </button>
        
        <p className="text-xs text-slate-500 text-center mt-4 flex items-center justify-center gap-1">
          <SafeIcon icon="FiLock" /> Your information is secure. We'll never share it with anyone.
        </p>
      </form>
    </div>
  );

  const progress = ((currentStep) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Quiz Header & Progress */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-2 flex items-center justify-between">
          <button onClick={handleBack} className="text-slate-500 hover:text-slate-800 flex items-center gap-1 font-medium transition-colors w-24">
            <SafeIcon icon="FiArrowLeft" /> Back
          </button>
          
          <div className="hidden md:flex justify-center flex-1">
            <img 
              src="https://media-manager-c.questera.ai/greta-media/525d05658fdf988acaa34e738d3f286826191483600217441074af5254e1324b9947d16ec384fcc03e2360d22f6a7d9b/images/aW1hZ2UvcG5n/312034cd79c028aad1e85c1e994b94aa.png" 
              alt="Pinoy General Insurance Logo" 
              className="h-16 md:h-20 object-contain"
            />
          </div>

          <div className="text-sm font-medium text-slate-500 w-24 text-right">
            {currentStep < questions.length ? `Step ${currentStep + 1} of ${questions.length}` : 'Final Step'}
          </div>
        </div>
        {/* Progress Bar */}
        <div className="w-full h-1.5 bg-slate-100">
          <div 
            className="h-full bg-[#10B981] transition-all duration-500 ease-out" 
            style={{ width: `${progress}%` }}
          />
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 overflow-hidden relative flex items-center justify-center p-6">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentStep}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="w-full"
          >
            {currentStep < questions.length ? renderQuestion() : renderLeadForm()}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}